<template>
  <div class="floating-contact">
    <!-- 플로팅 버튼 -->
    <button 
      class="floating-btn"
      :class="{ active: isOpen }"
      @click="toggleForm"
    >
      <span class="btn-icon" v-if="!isOpen">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      </span>
      <span class="btn-icon" v-else>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </span>
      <span class="btn-text">관심고객등록</span>
    </button>

    <!-- 폼 패널 -->
    <div class="contact-panel" :class="{ open: isOpen }">
      <div class="panel-header">
        <h3>관심고객등록</h3>
        <p>빠른 상담을 위해 정보를 남겨주세요</p>
      </div>

      <form @submit.prevent="submitForm" class="contact-form">
        <div class="form-group">
          <label for="floating-name">성함 <span class="required">*</span></label>
          <input 
            type="text" 
            id="floating-name" 
            v-model="form.name" 
            placeholder="성함을 입력해주세요"
            required
            :disabled="isSubmitting"
          />
        </div>

        <div class="form-group">
          <label>연락처 <span class="required">*</span></label>
          <div class="phone-inputs">
            <input
              type="tel"
              inputmode="numeric"
              ref="floatP1"
              v-model="form.phone1"
              placeholder="010"
              maxlength="3"
              required
              :disabled="isSubmitting"
              @input="onPhone('phone1', 3, 'floatP2')"
            />
            <span class="phone-dash">-</span>
            <input
              type="tel"
              inputmode="numeric"
              ref="floatP2"
              v-model="form.phone2"
              placeholder="0000"
              maxlength="4"
              required
              :disabled="isSubmitting"
              @input="onPhone('phone2', 4, 'floatP3')"
            />
            <span class="phone-dash">-</span>
            <input
              type="tel"
              inputmode="numeric"
              ref="floatP3"
              v-model="form.phone3"
              placeholder="0000"
              maxlength="4"
              required
              :disabled="isSubmitting"
              @input="onPhone('phone3', 4, null)"
            />
          </div>
        </div>

        <div class="form-group checkbox-group">
          <label class="checkbox-label">
            <input 
              type="checkbox" 
              v-model="form.agreePrivacy"
              required
              :disabled="isSubmitting"
            />
            <span class="checkmark"></span>
            <span class="checkbox-text">
              개인정보 수집 및 이용에 동의합니다.
              <button type="button" class="privacy-link" @click="showPrivacy = true">
                [보기]
              </button>
            </span>
          </label>
        </div>

        <button type="submit" class="submit-btn" :disabled="isSubmitting">
          <span v-if="isSubmitting">신청 중...</span>
          <span v-else>상담 신청하기</span>
        </button>
      </form>
    </div>

    <!-- 개인정보처리방침 모달 -->
    <div v-if="showPrivacy" class="modal-overlay" @click.self="showPrivacy = false">
      <div class="modal privacy-modal">
        <h2>개인정보 수집 및 이용 동의</h2>
        
        <div class="privacy-content">
          <p><strong>[개인정보취급방침 안내]</strong></p>
          <p>고객님의 개인정보를 중요시하며, "정보통신망 이용촉진 및 정보보호"에 관한 법률을 준수하고 있습니다.</p>
          <p>개인정보취급방침을 통하여 고객님께서 제공하시는 개인정보가 어떠한 용도와 방식으로 이용되고 있으며, 개인정보보호를 위해 어떠한 조치가 취해지고 있는지 알려드립니다.</p>
          
          <p><strong>[수집하는 개인정보의 항목]</strong></p>
          <p>- 수집항목: 성함, 연락처, 희망 상담일시</p>
          <p>- 개인정보 수집방법: 홈페이지(상담문의)</p>
          
          <p><strong>[개인정보의 수집 및 이용 목적]</strong></p>
          <p>- 서비스 제공에 관한 상담 및 안내</p>
          
          <p><strong>[개인정보의 보유 및 이용 기간]</strong></p>
          <p>원칙적으로, 개인정보 수집 및 이용목적이 달성된 후에는 해당 정보를 지체 없이 파기합니다.</p>
          <p>- 보존 항목: 성함, 연락처, 희망 상담일시</p>
          <p>- 보존 근거: 상담문의에 관한 기록</p>
        </div>
        
        <button 
          class="privacy-close-btn"
          @click="showPrivacy = false"
        >
          확인
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { registerCustomer } from '@/firebase/customerService'

export default {
  name: 'FloatingContact',
  data() {
    return {
      isOpen: false,
      showPrivacy: false,
      isSubmitting: false,
      form: {
        name: '',
        phone1: '010',
        phone2: '',
        phone3: '',
        agreePrivacy: false
      }
    }
  },
  computed: {
    fullPhone() {
      return `${this.form.phone1}-${this.form.phone2}-${this.form.phone3}`
    }
  },
  methods: {
    toggleForm() {
      this.isOpen = !this.isOpen
    },
    onPhone(field, maxLen, nextRef) {
      this.form[field] = this.form[field].replace(/[^0-9]/g, '')
      if (this.form[field].length >= maxLen && nextRef) {
        this.$refs[nextRef].focus()
      }
    },
    resetForm() {
      this.form = {
        name: '',
        phone1: '010',
        phone2: '',
        phone3: '',
        agreePrivacy: false
      }
    },
    async submitForm() {
      if (!this.form.agreePrivacy) {
        alert('개인정보 수집 및 이용에 동의해주세요.')
        return
      }

      this.isSubmitting = true

      try {
        await registerCustomer({
          name: this.form.name,
          phone: this.fullPhone
        }, 'pc-floating')

        alert('상담 신청이 완료되었습니다.\n담당자가 빠르게 연락드리겠습니다.')

        this.resetForm()
        this.isOpen = false
      } catch (error) {
        console.error('Submit error:', error)
        alert('신청 중 오류가 발생했습니다.\n잠시 후 다시 시도해주세요.')
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.floating-contact {
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 999;
}

/* 플로팅 버튼 */
.floating-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 28px;
  background: linear-gradient(135deg, #ff6b35 0%, #e63946 100%);
  color: #fff;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 700;
  box-shadow: 0 6px 25px rgba(230, 57, 70, 0.5);
  transition: all 0.3s;
}

.floating-btn:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 8px 30px rgba(230, 57, 70, 0.6);
}

.floating-btn.active {
  border-radius: 50%;
  padding: 16px;
  background: #333;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.floating-btn.active .btn-text {
  display: none;
}

.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-icon svg {
  width: 24px;
  height: 24px;
}

/* 폼 패널 */
.contact-panel {
  position: absolute;
  bottom: 75px;
  right: 0;
  width: 360px;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.2);
  opacity: 0;
  visibility: hidden;
  transform: translateY(20px) scale(0.95);
  transition: all 0.3s ease;
  overflow: hidden;
}

.contact-panel.open {
  opacity: 1;
  visibility: visible;
  transform: translateY(0) scale(1);
}

.panel-header {
  background: linear-gradient(135deg, #ff6b35 0%, #e63946 100%);
  color: #fff;
  padding: 28px;
  text-align: center;
}

.panel-header h3 {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 8px;
}

.panel-header p {
  font-size: 14px;
  opacity: 0.9;
}

/* 폼 스타일 */
.contact-form {
  padding: 28px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.phone-inputs {
  display: flex;
  align-items: center;
  gap: 6px;
}

.phone-inputs input {
  flex: 1;
  text-align: center;
  min-width: 0;
}

.phone-dash {
  color: #999;
  font-size: 16px;
  flex-shrink: 0;
}

.form-group {
  margin-bottom: 18px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.form-group .required {
  color: #e63946;
}

.form-group input[type="text"],
.form-group input[type="tel"],
.form-group input[type="date"],
.form-group input[type="time"],
.form-group textarea {
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e8e8e8;
  border-radius: 10px;
  font-size: 15px;
  transition: border-color 0.2s, box-shadow 0.2s;
  font-family: inherit;
  background: #fafafa;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #ff6b35;
  box-shadow: 0 0 0 4px rgba(255, 107, 53, 0.1);
  background: #fff;
}

.form-group input:disabled,
.form-group textarea:disabled {
  background: #f0f0f0;
  cursor: not-allowed;
}

.form-group textarea {
  resize: none;
}

/* 체크박스 */
.checkbox-group {
  margin-top: 22px;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  font-size: 13px;
  color: #555;
}

.checkbox-label input[type="checkbox"] {
  display: none;
}

.checkmark {
  display: inline-block;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  background: #fff;
  border: 2px solid #ccc;
  border-radius: 5px;
  position: relative;
  transition: all 0.2s;
}

.checkbox-label input[type="checkbox"]:checked + .checkmark {
  background: #e63946;
  border-color: #e63946;
}

.checkbox-label input[type="checkbox"]:checked + .checkmark::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 6px;
  width: 6px;
  height: 10px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.checkbox-text {
  line-height: 1.5;
}

.privacy-link {
  background: none;
  border: none;
  color: #e63946;
  cursor: pointer;
  font-size: 12px;
  padding: 0;
  text-decoration: underline;
  font-weight: 600;
}

.privacy-link:hover {
  color: #ff6b35;
}

/* 제출 버튼 */
.submit-btn {
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #ff6b35 0%, #e63946 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 12px;
}

.submit-btn:hover:not(:disabled) {
  box-shadow: 0 6px 20px rgba(230, 57, 70, 0.4);
  transform: translateY(-2px);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* 모달 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  padding: 20px;
}

.privacy-modal {
  background: #fff;
  border-radius: 20px;
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.privacy-modal h2 {
  font-size: 18px;
  font-weight: 700;
  color: #333;
  padding: 24px;
  border-bottom: 1px solid #eee;
  margin: 0;
}

.privacy-content {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.privacy-content p {
  font-size: 13px;
  color: #555;
  line-height: 1.7;
  margin-bottom: 12px;
}

.privacy-content p strong {
  color: #333;
}

.privacy-close-btn {
  margin: 0 24px 24px;
  padding: 14px;
  background: linear-gradient(135deg, #ff6b35 0%, #e63946 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.privacy-close-btn:hover {
  box-shadow: 0 4px 15px rgba(230, 57, 70, 0.4);
}

/* 반응형 */
@media (max-width: 768px) {
  .floating-contact {
    bottom: 20px;
    right: 20px;
  }

  .floating-btn {
    padding: 14px 22px;
    font-size: 15px;
  }

  .floating-btn.active {
    padding: 14px;
  }

  .btn-icon svg {
    width: 22px;
    height: 22px;
  }

  .contact-panel {
    width: calc(100vw - 40px);
    max-width: 360px;
    right: 0;
    bottom: 65px;
  }

  .panel-header {
    padding: 22px;
  }

  .panel-header h3 {
    font-size: 20px;
  }

  .contact-form {
    padding: 22px;
  }
}

@media (max-width: 400px) {
  .floating-btn .btn-text {
    display: none;
  }

  .floating-btn {
    border-radius: 50%;
    padding: 16px;
  }

  .contact-panel {
    position: fixed;
    bottom: 0;
    right: 0;
    left: 0;
    width: 100%;
    max-width: none;
    border-radius: 20px 20px 0 0;
    max-height: 85vh;
  }
}
</style>